import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageLoading } from "@/components/common/Loading";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtValue, RAG, BUSINESS_AREAS } from "@/lib/format";
import { toast } from "sonner";
import dayjs from "dayjs";

export default function CoachingDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [prep, setPrep] = useState(null);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get(`/coaching/${id}`).then((r) => {
      setSession(r.data);
      setNotes({
        discussion_notes: r.data.discussion_notes || "",
        decisions: r.data.decisions || "",
        observations: r.data.observations || "",
        client_comments: r.data.client_comments || "",
        next_session_date: r.data.next_session_date ? dayjs(r.data.next_session_date).format("YYYY-MM-DD") : "",
      });
    }).catch(() => navigate("/coaching"));
    api.get(`/coaching/${id}/prep`).then((r) => setPrep(r.data)).catch(() => {});
  };
  useEffect(() => { setSession(null); load(); }, [id]);

  if (!session) return <PageLoading />;

  const saveNotes = async (markComplete) => {
    setSaving(true);
    try {
      const payload = { ...notes };
      if (notes.next_session_date) payload.next_session_date = new Date(notes.next_session_date).toISOString();
      if (markComplete) payload.status = "completed";
      await api.patch(`/coaching/${id}`, payload);
      toast.success(markComplete ? "Session completed" : "Notes saved");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startExtract = async () => {
    const text = [notes.discussion_notes, notes.decisions, notes.observations, notes.client_comments].filter(Boolean).join("\n\n");
    if (!text.trim()) return toast.error("Add some notes first, then Amden can extract the follow-ups.");
    setExtracting(true);
    try {
      const { data } = await api.post(`/ai/extract-items?org_id=${orgId}`, { text });
      if (!data.items.length) { toast.info("Amden didn't find any clear commitments in the notes."); return; }
      setItems(data.items.map((it) => ({ ...it, selected: true })));
      setExtractOpen(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setExtracting(false); }
  };

  const createExtracted = async () => {
    const chosen = items.filter((i) => i.selected);
    if (!chosen.length) return;
    setCreating(true);
    try {
      let plan = null;
      if (chosen.some((i) => i.type === "priority")) {
        const { data: plans } = await api.get(`/plans?org_id=${orgId}`);
        plan = plans.find((p) => ["active", "at_risk"].includes(p.status)) || plans[0] || null;
      }
      let created = 0;
      for (const it of chosen) {
        if (it.type === "action" || !plan) {
          await api.post(`/coaching/${id}/actions`, {
            title: it.title, description: it.description, business_area: it.business_area || BUSINESS_AREAS[0],
            due_date: it.due_date || "", priority_level: it.priority_level, status: "not_started",
          });
        } else {
          await api.post(`/priorities?org_id=${orgId}`, {
            plan_id: plan.id, title: it.title, business_area: it.business_area,
            why_it_matters: it.why_it_matters, desired_outcome: it.desired_outcome, success_measure: it.success_measure,
          });
        }
        created += 1;
      }
      toast.success(`Created ${created} item(s) from the meeting`);
      setExtractOpen(false); setItems([]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div data-testid="coaching-detail-page">
      <button onClick={() => navigate("/coaching")} className="mb-4 flex items-center gap-1.5 text-sm text-[#1E2D44]/55 hover:text-[#FFB000]"><Icons.ArrowLeft size={15} /> All sessions</button>

      <div className="amden-card mb-6 p-6 lg:p-7">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <RagBadge rag={session.status === "completed" ? "green" : session.status === "preparation_required" ? "amber" : "blue"}>
                {session.status.replace(/_/g, " ")}
              </RagBadge>
              <span className="text-xs text-[#1E2D44]/50">{fmtDate(session.date)} · {dayjs(session.date).format("HH:mm")} · {session.meeting_type}</span>
            </div>
            <h1 className="mt-1.5 font-display text-2xl font-bold text-[#0B1729]">{session.title}</h1>
            <p className="mt-1 text-sm text-[#1E2D44]/60">{session.objective}</p>
            <div className="mt-2 text-xs text-[#1E2D44]/50">with {session.consultant_name}{session.location ? ` · ${session.location}` : ""}</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="prep">
        <TabsList className="mb-5 bg-[#E4E8EF]">
          <TabsTrigger value="prep" data-testid="tab-prep">Preparation</TabsTrigger>
          <TabsTrigger value="workspace" data-testid="tab-workspace">Workspace</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
        </TabsList>

        {/* Preparation */}
        <TabsContent value="prep">
          {!prep ? <PageLoading /> : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <MiniStat label="Health score" value={prep.health_score ?? "—"} icon="Activity" />
                <MiniStat label="Completed actions" value={prep.completed_actions.length} icon="CircleCheck" tone="green" />
                <MiniStat label="Overdue actions" value={prep.overdue_actions.length} icon="TriangleAlert" tone={prep.overdue_actions.length ? "red" : "green"} />
                <MiniStat label="Decisions required" value={prep.decisions_required.length} icon="GitBranch" tone={prep.decisions_required.length ? "amber" : "green"} />
              </div>

              <div className="amden-card p-6">
                <h3 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><Icons.Sparkles size={16} className="text-[#FFB000]" /> Suggested discussion points</h3>
                {prep.suggested_points.length ? (
                  <ul className="space-y-2">
                    {prep.suggested_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1E2D44]/75"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FFB000]" />{p}</li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-[#1E2D44]/50">Nothing flagged — a good position to be in.</p>}
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="amden-card p-6">
                  <h3 className="mb-3 font-display text-base font-bold">Overdue actions</h3>
                  {prep.overdue_actions.length ? prep.overdue_actions.map((a) => (
                    <div key={a.id} className="mb-2 flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                      <span className="text-sm text-[#0B1729]">{a.title}</span>
                      <span className="text-xs text-[#C95555]">{fmtDate(a.due_date)}</span>
                    </div>
                  )) : <p className="text-sm text-[#1E2D44]/50">None overdue.</p>}
                </div>
                <div className="amden-card p-6">
                  <h3 className="mb-3 font-display text-base font-bold">Declining KPIs</h3>
                  {prep.declining_kpis.length ? prep.declining_kpis.map((k) => (
                    <div key={k.id} className="mb-2 flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                      <span className="text-sm text-[#0B1729]">{k.name}</span>
                      <RagBadge rag={k.status}>{fmtValue(k.current_value, k.unit)}</RagBadge>
                    </div>
                  )) : <p className="text-sm text-[#1E2D44]/50">No declining KPIs.</p>}
                </div>
              </div>

              {prep.open_issues.length > 0 && (
                <div className="amden-card p-6">
                  <h3 className="mb-3 font-display text-base font-bold">Open issues</h3>
                  {prep.open_issues.map((i) => (
                    <div key={i.id} className="mb-2 flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                      <span className="text-sm text-[#0B1729]">{i.title}</span>
                      <RagBadge rag={i.urgency === "high" || i.urgency === "critical" ? "red" : "amber"}>{i.status.replace(/_/g, " ")}</RagBadge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Workspace */}
        <TabsContent value="workspace">
          {canEdit ? (
            <div className="space-y-4">
              <div className="flex justify-end gap-2">
                <Button onClick={startExtract} disabled={extracting} variant="outline" className="border-[#E4E8EF] hover:border-[#FFB000] hover:text-[#FFB000]" data-testid="extract-actions-btn">
                  {extracting ? <Icons.Loader2 className="animate-spin mr-1.5" size={15} /> : <Icons.Sparkles size={15} className="mr-1.5 text-[#FFB000]" />} Extract with Amden
                </Button>
                <Button onClick={() => setActionOpen(true)} variant="outline" className="border-[#E4E8EF] hover:border-[#FFB000] hover:text-[#FFB000]" data-testid="create-action-from-session">
                  <Icons.Plus size={15} className="mr-1.5" /> Create action from meeting
                </Button>
              </div>
              <WorkspaceField label="Discussion notes" value={notes.discussion_notes} onChange={(v) => setNotes({ ...notes, discussion_notes: v })} rows={5} testId="notes-discussion" />
              <WorkspaceField label="Decisions made" value={notes.decisions} onChange={(v) => setNotes({ ...notes, decisions: v })} rows={3} testId="notes-decisions" />
              <WorkspaceField label="Consultant observations" value={notes.observations} onChange={(v) => setNotes({ ...notes, observations: v })} rows={3} testId="notes-observations" />
              <WorkspaceField label="Client comments" value={notes.client_comments} onChange={(v) => setNotes({ ...notes, client_comments: v })} rows={2} testId="notes-client" />
              <div className="amden-card p-5">
                <Label>Next session date</Label>
                <Input type="date" value={notes.next_session_date} onChange={(e) => setNotes({ ...notes, next_session_date: e.target.value })} className="mt-1.5 max-w-xs" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveNotes(false)} disabled={saving} variant="outline" className="border-[#E4E8EF]" data-testid="save-notes-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save notes"}</Button>
                <Button onClick={() => saveNotes(true)} disabled={saving} className="bg-[#3D8B65] hover:bg-[#347556]" data-testid="complete-session-btn"><Icons.CircleCheck size={16} className="mr-1.5" /> Save & complete session</Button>
              </div>
            </div>
          ) : <p className="text-sm text-[#1E2D44]/50">Read-only access.</p>}
        </TabsContent>

        {/* Summary */}
        <TabsContent value="summary">
          <div className="amden-card p-6 lg:p-8">
            <div className="mb-6 flex items-center justify-between border-b border-[#EEF1F6] pb-4">
              <div>
                <div className="label-eyebrow mb-1">Coaching summary</div>
                <h2 className="font-display text-xl font-bold">{session.title}</h2>
                <div className="text-sm text-[#1E2D44]/55">{fmtDate(session.date)} · {session.consultant_name}</div>
              </div>
              <Button variant="outline" onClick={() => window.print()} className="border-[#E4E8EF]"><Icons.Printer size={16} className="mr-1.5" /> Print</Button>
            </div>
            <SummaryBlock title="Objective" text={session.objective} />
            <SummaryBlock title="Key discussion points" text={session.discussion_notes} />
            <SummaryBlock title="Decisions made" text={session.decisions} />
            <SummaryBlock title="Consultant observations" text={session.observations} />
            <SummaryBlock title="Client comments" text={session.client_comments} />
            {session.created_actions?.length > 0 && (
              <div className="mb-5">
                <div className="label-eyebrow mb-2">Agreed actions</div>
                {session.created_actions.map((a) => (
                  <div key={a.id} className="mb-2 flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                    <span className="text-sm text-[#0B1729]">{a.title}</span>
                    <span className="text-xs text-[#1E2D44]/50">Due {fmtDate(a.due_date)}</span>
                  </div>
                ))}
              </div>
            )}
            {session.next_session_date && <SummaryBlock title="Next session" text={fmtDate(session.next_session_date)} />}
          </div>
        </TabsContent>
      </Tabs>

      {actionOpen && <SessionActionDialog sessionId={id} onClose={() => setActionOpen(false)} onSaved={() => { setActionOpen(false); load(); toast.success("Action created from meeting"); }} />}

      <Dialog open={extractOpen} onOpenChange={setExtractOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="extract-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[#FFB000]"><Icons.Sparkles size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Extracted by Amden</span></div>
            <DialogTitle className="font-display text-xl">Follow-ups from this meeting</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#1E2D44]/60">Review what Amden picked up. Untick anything you don't want, then create them all at once.</p>
          <div className="space-y-2.5">
            {items.map((it, i) => (
              <label key={i} className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#E4E8EF] p-3.5 transition-colors hover:border-[#FFC24D]" data-testid={`extract-item-${i}`}>
                <input type="checkbox" checked={it.selected} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, selected: e.target.checked } : x)))} className="mt-1 h-4 w-4 accent-[#FFB000]" data-testid={`extract-check-${i}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${it.type === "priority" ? "bg-[#0B1729] text-[#FFC24D]" : "bg-[#FFB000]/15 text-[#B87F1E]"}`}>{it.type}</span>
                    <span className="font-semibold text-[#0B1729]">{it.title}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#1E2D44]/55">
                    {it.business_area && <span>{it.business_area}</span>}
                    {it.type === "action" && it.due_date && <span>Due {fmtDate(it.due_date)}</span>}
                    {it.type === "action" && <span>Priority: {it.priority_level}</span>}
                  </div>
                  {it.description && <p className="mt-1 text-xs text-[#1E2D44]/65">{it.description}</p>}
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtractOpen(false)}>Cancel</Button>
            <Button onClick={createExtracted} disabled={creating || !items.some((i) => i.selected)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="create-extracted-btn">
              {creating ? <Icons.Loader2 className="animate-spin" size={16} /> : `Create ${items.filter((i) => i.selected).length} item(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, icon, tone = "default" }) {
  const Icon = Icons[icon] || Icons.Circle;
  const tones = { default: "text-[#FFB000] bg-[#FFB000]/10", green: "text-[#3D8B65] bg-[#3D8B65]/10", amber: "text-[#B87F1E] bg-[#D99A32]/12", red: "text-[#C95555] bg-[#C95555]/10" };
  return (
    <div className="amden-card p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#1E2D44]/50">{label}</div>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tones[tone]}`}><Icon size={15} /></span>
      </div>
      <div className="mt-1 font-display text-2xl font-bold text-[#0B1729]">{value}</div>
    </div>
  );
}
function WorkspaceField({ label, value, onChange, rows, testId }) {
  return (
    <div className="amden-card p-5">
      <Label className="text-sm">{label}</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-2" data-testid={testId} />
    </div>
  );
}
function SummaryBlock({ title, text }) {
  return (
    <div className="mb-5">
      <div className="label-eyebrow mb-1">{title}</div>
      <p className="whitespace-pre-wrap text-sm text-[#1E2D44]/80">{text || "—"}</p>
    </div>
  );
}

function SessionActionDialog({ sessionId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", business_area: BUSINESS_AREAS[0], due_date: "", priority_level: "normal", status: "not_started" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try { await api.post(`/coaching/${sessionId}/actions`, form); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create action from meeting</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="session-action-title" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Business area</Label>
              <Select value={form.business_area} onValueChange={(v) => setForm({ ...form, business_area: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-session-action">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Create action"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
